import React, { useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { FocusIcon } from '@atlaskit/logo';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/compiled';
import {
	getProductAndIDFromARI,
	type SearchResultType as SearchClientResultType,
} from '@atlassian/search-client';

import { ProductKeys } from '../../../../../../common/constants/products';
import type { SearchResultProps } from '../../../../../../common/types';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import { enrichExcerpt } from '../../../../../../common/utils/search-result-utils';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchMercuryFocusArea_AtlassianSearchPage$key } from './__generated__/searchMercuryFocusArea_AtlassianSearchPage.graphql';
import { SearchMercuryFocusAreaStatusTargetDate } from './search-mercury-focus-area-status-target-date';

export const SearchMercuryFocusArea = ({
	node,
	position,
	cloudId,
	selectedProduct,
	primaryProduct,
	appMode,
	mainContentWidth,
}: SearchResultProps<searchMercuryFocusArea_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchMercuryFocusArea_AtlassianSearchPage on SearchResult {
				...searchMercuryFocusAreaStatusTargetDate_AtlassianSearchPage
				__typename

				id
				title
				type
				url

				... on SearchResultMercuryFocusArea {
					lastModifiedDate
					focusArea {
						id
						name
						focusAreaType {
							id
							name
						}

						owner {
							name
						}
					}
				}
			}
		`,
		node,
	);

	const metadata = useMemo(() => {
		const [product, contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type]);

	const onResultClicked = useFireResultClicked({ metadata, position });
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({ metadata, position });
	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const title = useMemo(() => enrichExcerpt(data.title), [data.title]);
	const headerMetadataParts = useMemo(
		() => [
			[
				'type',
				<Box>
					<SearchResultType type={data.type as SearchClientResultType} />
				</Box>,
			],
			['focusAreaType', <Box>{data.focusArea?.focusAreaType?.name}</Box>],
			data.focusArea?.owner?.name ? ['owner', <Box>{data.focusArea?.owner?.name}</Box>] : null,
			[
				'last-modified',
				data.lastModifiedDate ? (
					<Box>
						<LastModified lastModified={data.lastModifiedDate} />
					</Box>
				) : null,
			],
		],
		[
			data.type,
			data.focusArea?.focusAreaType?.name,
			data.focusArea?.owner?.name,
			data.lastModifiedDate,
		],
	);

	return (
		<SearchResult
			{...data}
			product={ProductKeys.Focus}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={data.url}
			title={title}
			icon={<FocusIcon size="medium" shouldUseNewLogoDesign />}
			headerMetadataParts={headerMetadataParts}
			description={<SearchMercuryFocusAreaStatusTargetDate node={data} />}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};

export const isSearchMercuryFocusArea = (
	node?: any,
): node is searchMercuryFocusArea_AtlassianSearchPage$key => {
	return node?.id?.includes('mercury') && node.__typename === 'SearchResultMercuryFocusArea';
};
